/*
    Be aware that this component, in terms of layout, is almost a copy of OfferBox.
    In case of making any changes keep in mind to update OfferBox also.

    TODO: Maybe it's worth to make some abstraction/shared component if there are more cases like this
 */
import React, {memo} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, p, pr, truncate, truncateMultiline} from "@pg-design/helpers-css-strings";
import {Image} from "@pg-design/image-module";
import {Highlight} from "@pg-design/text-module";
import {numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {offerBoxImgStyle, offerBoxImgWrapperStyle} from "../../atoms/OfferBoxBase";
import {DesktopText} from "../../atoms/typography/DesktopText";
import {NoTagPlug} from "../../offer/components/offer_box/NoTagPlug";
import {OfferBoxVendorImage} from "../../offer/components/offer_box/OfferBoxVendorImage";
import {PrimaryBadge} from "../../offer/components/offer_box/PrimaryBadge";
import {getOfferAddressText} from "../../offer/utils/get_offer_address_text";
import {nativeOfferClickHit} from "../../tracking/algolytics_hits/sponsor_offer_click_hit";
import {ICreation} from "../types/ICreation";

interface IProps {
    creation: ISponsoredOfferBoxCreation;
    url: string;
}

/*
    In case below interface gets updated, remember to also update previews in NPH
 */
export type ISponsoredOfferBoxCreation = Pick<
    ICreation,
    | "desktop_distance"
    | "mobile_address"
    | "mobile_region"
    | "desktop_vendor_logo"
    | "mobile_image_native"
    | "mobile_name"
    | "mobile_construction_end_date"
    | "mobile_individual_price_m2"
    | "mobile_vendor"
>;

export const SponsoredOfferBox = memo((props: IProps) => {
    const {creation, url} = props;
    const {isMobile} = useUserDevice();
    const mainImageMobile = creation.mobile_image_native?.m_img_375x211;
    const mainImageDesktop = creation.mobile_image_native && "m_img_500" in creation.mobile_image_native && creation.mobile_image_native?.m_img_500;
    const mainImage = isMobile ? mainImageMobile : mainImageDesktop || mainImageMobile;

    const onNativeCreationClick = () => {
        nativeOfferClickHit(url);
        window.open(url, "_blank");
    };

    const distanceFromCenter = creation.desktop_distance ? creation.desktop_distance : null;

    const addressText = getOfferAddressText({
        street_name: creation.mobile_address,
        street_number: null,
        region: {
            short_name_reverted: creation.mobile_region
        }
    });

    const vendorLogoSrc: string | null = creation.desktop_vendor_logo ? creation.desktop_vendor_logo.v_log_80x60 : null;

    return (
        <OfferBoxBase onClick={onNativeCreationClick} css={offerBoxStyle}>
            <div css={offerBoxLink}>
                <Image
                    src={mainImage}
                    alt={creation.mobile_name}
                    width="375px"
                    height="211px"
                    loading="lazy"
                    css={imgWrapperStyle}
                    imageStyle={offerBoxImgStyle}
                />

                {creation.mobile_construction_end_date && <PrimaryBadge css={badge}>{creation.mobile_construction_end_date}</PrimaryBadge>}

                <Badge css={bottomBadge} variant="label_warning">
                    Oferta sponsorowana
                </Badge>
            </div>

            <div css={offerBoxInfoHolder}>
                <div>
                    <div css={baseInfoWrapperStyle}>
                        <DesktopText variant="headline_6" as="h2" css={nameStyle}>
                            {creation.mobile_name}
                        </DesktopText>

                        {creation.mobile_individual_price_m2 ? (
                            <DesktopText variant="body_copy_2" as="div" strong>
                                <Highlight>
                                    od{" "}
                                    <span className="bt">
                                        {numberFormat(parseInt(creation.mobile_individual_price_m2, 10))} zł/m<sup>2</sup>
                                    </span>
                                </Highlight>
                            </DesktopText>
                        ) : (
                            <NoTagPlug height="2rem" />
                        )}
                    </div>

                    <div css={addressAndLogoHolder}>
                        <div css={detailsWrapperStyle}>
                            <DesktopText css={noAddressTextPlug} variant="body_copy_2" className="bt">
                                {addressText}
                            </DesktopText>

                            {distanceFromCenter ? (
                                <DesktopText variant="body_copy_2" strong>
                                    ({distanceFromCenter} km od centrum)
                                </DesktopText>
                            ) : (
                                <NoTagPlug height="2rem" />
                            )}
                        </div>

                        <OfferBoxVendorImage unlinkVendor vendorLogo={vendorLogoSrc} vendorName={creation.mobile_vendor} />
                    </div>
                </div>
            </div>

            <div css={buttonWrapperStyle}>
                <Button css={buttonStyle} variant="filled_primary">
                    Zobacz inwestycję
                </Button>
            </div>
        </OfferBoxBase>
    );
});

const offerBoxStyle = css`
    height: 100%;
`;

export const OfferBoxBase = styled.div`
    ${elevation(2)}
    width: 100%;
    min-width: 288px;
    max-width: 375px;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    transition: box-shadow 0.2s ease-in-out;
    ${borderRadius(2, 2, 2, 2)}
    margin: 0 auto;

    &:hover {
        ${elevation(3)}
    }
`;

const INFO_PANEL_PADDING = "1.2rem";

const badge = css`
    position: absolute;
    top: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

const bottomBadge = css`
    position: absolute;
    bottom: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

export const offerBoxLink = css`
    display: block;
    position: relative;
    cursor: pointer;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const imgWrapperStyle = css`
    display: block;
    ${offerBoxImgWrapperStyle};
    ${borderRadius(2, 2, 0, 0)};
`;

const offerBoxInfoHolder = css`
    ${p(2)};
    position: relative;
    min-height: 17.3rem;
    height: 100%;
`;

const addressAndLogoHolder = css`
    display: flex;
    padding-top: 1.6rem;
    justify-content: space-between;
    align-items: center;
    min-height: 6rem;
`;

const noAddressTextPlug = css`
    height: 4rem;
    ${truncateMultiline(2)};
`;

const baseInfoWrapperStyle = css`
    cursor: pointer;
`;

const nameStyle = css`
    ${truncate};
`;

const detailsWrapperStyle = css`
    ${pr()};
    cursor: pointer;
`;

const buttonWrapperStyle = css`
    ${p(0, 2, 2, 2)};
`;

const buttonStyle = css`
    width: 100%;
`;
/*
    Be aware that this component, in terms of layout, is almost a copy of OfferBox.
    In case of making any changes keep in mind to update OfferBox also.

    TODO: Maybe it's worth to make some abstraction/shared component if there are more cases like this
 */
import React, {memo} from "react";
import {css} from "@emotion/react";
import styled from "@emotion/styled";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {elevation} from "@pg-design/elevation";
import {borderRadius, p, pr, truncate, truncateMultiline} from "@pg-design/helpers-css-strings";
import {Image} from "@pg-design/image-module";
import {Highlight} from "@pg-design/text-module";
import {numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {offerBoxImgStyle, offerBoxImgWrapperStyle} from "../../atoms/OfferBoxBase";
import {DesktopText} from "../../atoms/typography/DesktopText";
import {NoTagPlug} from "../../offer/components/offer_box/NoTagPlug";
import {OfferBoxVendorImage} from "../../offer/components/offer_box/OfferBoxVendorImage";
import {PrimaryBadge} from "../../offer/components/offer_box/PrimaryBadge";
import {getOfferAddressText} from "../../offer/utils/get_offer_address_text";
import {nativeOfferClickHit} from "../../tracking/algolytics_hits/sponsor_offer_click_hit";
import {ICreation} from "../types/ICreation";

interface IProps {
    creation: ISponsoredOfferBoxCreation;
    url: string;
}

/*
    In case below interface gets updated, remember to also update previews in NPH
 */
export type ISponsoredOfferBoxCreation = Pick<
    ICreation,
    | "desktop_distance"
    | "mobile_address"
    | "mobile_region"
    | "desktop_vendor_logo"
    | "mobile_image_native"
    | "mobile_name"
    | "mobile_construction_end_date"
    | "mobile_individual_price_m2"
    | "mobile_vendor"
>;

export const SponsoredOfferBox = memo((props: IProps) => {
    const {creation, url} = props;
    const {isMobile} = useUserDevice();
    const mainImageMobile = creation.mobile_image_native?.m_img_375x211;
    const mainImageDesktop = creation.mobile_image_native && "m_img_500" in creation.mobile_image_native && creation.mobile_image_native?.m_img_500;
    const mainImage = isMobile ? mainImageMobile : mainImageDesktop || mainImageMobile;

    const onNativeCreationClick = () => {
        nativeOfferClickHit(url);
        window.open(url, "_blank");
    };

    const distanceFromCenter = creation.desktop_distance ? creation.desktop_distance : null;

    const addressText = getOfferAddressText({
        street_name: creation.mobile_address,
        street_number: null,
        region: {
            short_name_reverted: creation.mobile_region
        }
    });

    const vendorLogoSrc: string | null = creation.desktop_vendor_logo ? creation.desktop_vendor_logo.v_log_80x60 : null;

    return (
        <OfferBoxBase onClick={onNativeCreationClick} css={offerBoxStyle}>
            <div css={offerBoxLink}>
                <Image
                    src={mainImage}
                    alt={creation.mobile_name}
                    width="375px"
                    height="211px"
                    loading="lazy"
                    css={imgWrapperStyle}
                    imageStyle={offerBoxImgStyle}
                />

                {creation.mobile_construction_end_date && <PrimaryBadge css={badge}>{creation.mobile_construction_end_date}</PrimaryBadge>}

                <Badge css={bottomBadge} variant="label_warning">
                    Oferta sponsorowana
                </Badge>
            </div>

            <div css={offerBoxInfoHolder}>
                <div>
                    <div css={baseInfoWrapperStyle}>
                        <DesktopText variant="headline_6" as="h2" css={nameStyle}>
                            {creation.mobile_name}
                        </DesktopText>

                        {creation.mobile_individual_price_m2 ? (
                            <DesktopText variant="body_copy_2" as="div" strong>
                                <Highlight>
                                    od{" "}
                                    <span className="bt">
                                        {numberFormat(parseInt(creation.mobile_individual_price_m2, 10))} zł/m<sup>2</sup>
                                    </span>
                                </Highlight>
                            </DesktopText>
                        ) : (
                            <NoTagPlug height="2rem" />
                        )}
                    </div>

                    <div css={addressAndLogoHolder}>
                        <div css={detailsWrapperStyle}>
                            <DesktopText css={noAddressTextPlug} variant="body_copy_2" className="bt">
                                {addressText}
                            </DesktopText>

                            {distanceFromCenter ? (
                                <DesktopText variant="body_copy_2" strong>
                                    ({distanceFromCenter} km od centrum)
                                </DesktopText>
                            ) : (
                                <NoTagPlug height="2rem" />
                            )}
                        </div>

                        <OfferBoxVendorImage unlinkVendor vendorLogo={vendorLogoSrc} vendorName={creation.mobile_vendor} />
                    </div>
                </div>
            </div>

            <div css={buttonWrapperStyle}>
                <Button css={buttonStyle} variant="filled_primary">
                    Zobacz inwestycję
                </Button>
            </div>
        </OfferBoxBase>
    );
});

const offerBoxStyle = css`
    height: 100%;
`;

export const OfferBoxBase = styled.div`
    ${elevation(2)}
    width: 100%;
    min-width: 288px;
    max-width: 375px;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    transition: box-shadow 0.2s ease-in-out;
    ${borderRadius(2, 2, 2, 2)}
    margin: 0 auto;

    &:hover {
        ${elevation(3)}
    }
`;

const INFO_PANEL_PADDING = "1.2rem";

const badge = css`
    position: absolute;
    top: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

const bottomBadge = css`
    position: absolute;
    bottom: ${INFO_PANEL_PADDING};
    left: ${INFO_PANEL_PADDING};
`;

export const offerBoxLink = css`
    display: block;
    position: relative;
    cursor: pointer;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const imgWrapperStyle = css`
    display: block;
    ${offerBoxImgWrapperStyle};
    ${borderRadius(2, 2, 0, 0)};
`;

const offerBoxInfoHolder = css`
    ${p(2)};
    position: relative;
    min-height: 17.3rem;
    height: 100%;
`;

const addressAndLogoHolder = css`
    display: flex;
    padding-top: 1.6rem;
    justify-content: space-between;
    align-items: center;
    min-height: 6rem;
`;

const noAddressTextPlug = css`
    height: 4rem;
    ${truncateMultiline(2)};
`;

const baseInfoWrapperStyle = css`
    cursor: pointer;
`;

const nameStyle = css`
    ${truncate};
`;

const detailsWrapperStyle = css`
    ${pr()};
    cursor: pointer;
`;

const buttonWrapperStyle = css`
    ${p(0, 2, 2, 2)};
`;

const buttonStyle = css`
    width: 100%;
`;
